import { differenceInWeeks, startOfWeek, addWeeks, lastDayOfMonth, getDay, subDays, format, parseISO, differenceInCalendarMonths, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';

export const calculateWeeks = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
  const weeks = differenceInWeeks(end, start);
  return Math.max(1, Math.ceil(weeks) + 1);
};

export const getWeekLabels = (startDate, numWeeks) => {
  if (!startDate) return [];
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const labels = [];
  for (let i = 0; i < numWeeks; i++) {
    const weekStart = addWeeks(start, i);
    labels.push(`W${i + 1} (${format(weekStart, 'MMM d')})`);
  }
  return labels;
};

export const getProjectDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
  const weeks = calculateWeeks(startDate, endDate);
  const months = differenceInCalendarMonths(end, start);
  if (months > 0) {
    return `${months} month${months > 1 ? 's' : ''}, ${weeks} week${weeks > 1 ? 's' : ''}`;
  }
  return `${weeks} week${weeks > 1 ? 's' : ''}`;
};

export const getLastFridayOfMonth = (year, month) => {
  const lastDay = lastDayOfMonth(new Date(year, month));
  const dayOfWeek = getDay(lastDay);
  const diff = dayOfWeek >= 5 ? dayOfWeek - 5 : dayOfWeek + 2;
  return subDays(lastDay, diff);
};

export const generateMonthlyInvoices = (startDate, endDate, resources, numWeeks) => {
  if (!startDate || !endDate || !resources.length) return [];

  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
  const totalMonths = differenceInCalendarMonths(end, start) + 1;
  
  const invoices = [];
  let cumulativeInvoiced = 0;

  const totalEstimate = resources.reduce((sum, res) => {
    const resTotal = (res.weeklyAllocation || []).reduce((s, val) => s + (val || 0), 0) * (res.rate || 0);
    return sum + resTotal;
  }, 0);

  for (let m = 0; m < totalMonths; m++) {
    const monthStart = startOfMonth(new Date(start.getFullYear(), start.getMonth() + m));
    const monthEnd = endOfMonth(monthStart);
    
    let monthValue = 0;
    for (let w = 0; w < numWeeks; w++) {
      const weekStart = addWeeks(start, w);
      if (isWithinInterval(weekStart, { start: monthStart, end: monthEnd })) {
        resources.forEach(res => {
          const allocation = (res.weeklyAllocation || [])[w] || 0;
          monthValue += allocation * (res.rate || 0);
        });
      }
    }

    const invoiceDate = getLastFridayOfMonth(monthStart.getFullYear(), monthStart.getMonth());

    cumulativeInvoiced += monthValue;

    invoices.push({
      month: format(monthStart, 'MMMM yyyy'),
      monthKey: format(monthStart, 'yyyy-MM'),
      invoiceValue: Math.round(monthValue),
      consumptionAmount: Math.round(monthValue),
      totalEstimate: Math.round(totalEstimate),
      remainingValue: Math.round(totalEstimate - cumulativeInvoiced),
      invoiceDate: format(invoiceDate, 'yyyy-MM-dd'),
      invoiceDateFormatted: format(invoiceDate, 'MMM d, yyyy'),
      invoiceNumber: '',
      status: 'po_awaited',
    });
  }

  return invoices;
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
